import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalAlertComponent } from 'src/app/shared/modal-alert/modal-alert.component';
import { CementoPaymentService } from 'src/app/services/cemento-payment.service';

@Component({
  selector: 'app-cemento-pago',
  template: '',
})
export class CementoPagoComponent implements OnInit, OnDestroy {
  private orderCode = '';
  private mercadoPagoConfirmationStartedAt = 0;
  private mercadoPagoWebhookGraceSeconds = 20;
  private mercadoPagoConfirmationTimer?: ReturnType<typeof setTimeout>;
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly spinner: NgxSpinnerService,
    private readonly modalService: NgbModal,
    private readonly paymentService: CementoPaymentService
  ) {}

  ngOnInit(): void {
    const orderCode = String(this.route.snapshot.paramMap.get('orderId') ?? '').trim();
    this.orderCode = orderCode;
    const isConfirmation = this.route.snapshot.url.some((segment) => segment.path.includes('confirmacion'));
    if (!orderCode) {
      this.router.navigateByUrl('/cemento/pago');
      return;
    }

    const provider = this.getPaymentProvider();
    if (isConfirmation && provider === 'mercadopago') {
      this.startMercadoPagoConfirmation(orderCode);
      return;
    }

    this.spinner.show();
    const request = isConfirmation
      ? this.paymentService.confirmPayment(orderCode, provider, this.buildPaymentNotificationPayload(orderCode, provider))
      : this.paymentService.cancelPayment(orderCode, provider, this.buildPaymentNotificationPayload(orderCode, provider));

    request.subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.redirect_url) {
          location.href = response.redirect_url;
          return;
        }
        this.showModal(
          isConfirmation ? 'Pago confirmado' : 'Pago cancelado',
          isConfirmation
            ? 'El pago de cemento se procesó correctamente.'
            : 'El pago de cemento fue cancelado.',
          () => this.navigateToOrderSummary(orderCode)
        );
      },
      error: () => {
        this.spinner.hide();
        this.showModal(
          'Error',
          'No pudimos procesar la respuesta del medio de pago.',
          () => this.navigateToOrderSummary(orderCode)
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.clearMercadoPagoConfirmationTimer();
  }

  private startMercadoPagoConfirmation(resumeToken: string): void {
    this.spinner.show();
    this.mercadoPagoConfirmationStartedAt = Date.now();
    this.paymentService.getMercadoPagoConfirmationConfig(resumeToken).subscribe({
      next: (config) => {
        this.mercadoPagoWebhookGraceSeconds = config.webhook_grace_seconds;
        this.checkMercadoPagoConfirmation(resumeToken);
      },
      error: () => this.returnToOrderSummary(),
    });
  }

  private checkMercadoPagoConfirmation(resumeToken: string): void {
    this.paymentService.getMercadoPagoConfirmationStatus(resumeToken).subscribe({
      next: (response) => {
        if (response.final) {
          this.returnToOrderSummary();
          return;
        }
        const elapsedSeconds = (Date.now() - this.mercadoPagoConfirmationStartedAt) / 1000;
        if (elapsedSeconds >= this.mercadoPagoWebhookGraceSeconds) {
          this.returnToOrderSummary();
          return;
        }
        this.mercadoPagoConfirmationTimer = setTimeout(
          () => this.checkMercadoPagoConfirmation(resumeToken),
          Math.max(2000, response.retry_after_seconds * 1000),
        );
      },
      error: () => this.returnToOrderSummary(),
    });
  }

  returnToOrderSummary(): void {
    this.clearMercadoPagoConfirmationTimer();
    if (!this.orderCode) {
      this.router.navigateByUrl('/cemento/pago');
      return;
    }
    this.paymentService.confirmPayment(this.orderCode, 'mercadopago').subscribe({
      next: (response) => {
        if (response.redirect_url) {
          this.spinner.hide();
          location.href = response.redirect_url;
          return;
        }
        this.navigateToOrderSummary(this.orderCode);
      },
      error: () => this.navigateToOrderSummary(this.orderCode),
    });
  }

  private navigateToOrderSummary(orderCode: string): void {
    this.clearMercadoPagoConfirmationTimer();
    this.spinner.hide();
    this.router.navigateByUrl(`/cemento/pedido/${encodeURIComponent(orderCode)}`);
  }

  private clearMercadoPagoConfirmationTimer(): void {
    if (this.mercadoPagoConfirmationTimer) {
      clearTimeout(this.mercadoPagoConfirmationTimer);
      this.mercadoPagoConfirmationTimer = undefined;
    }
  }

  private getPaymentProvider(): 'gnet' | 'mercadopago' {
    const provider = String(this.route.snapshot.queryParamMap.get('provider') ?? '').trim().toLowerCase();
    return provider === 'mercadopago' ? 'mercadopago' : 'gnet';
  }

  private buildPaymentNotificationPayload(orderCode: string, provider: 'gnet' | 'mercadopago') {
    if (provider !== 'mercadopago') {
      return {
        order_code: orderCode,
      };
    }

    const queryParams = this.route.snapshot.queryParams;
    const payload = {
      order_code: orderCode,
      status: queryParams['status'] ?? '',
      collection_status: queryParams['collection_status'] ?? '',
      payment_id: queryParams['payment_id'] ?? '',
      collection_id: queryParams['collection_id'] ?? '',
      external_reference: queryParams['external_reference'] ?? '',
      payment_type: queryParams['payment_type'] ?? '',
      merchant_order_id: queryParams['merchant_order_id'] ?? '',
      preference_id: queryParams['preference_id'] ?? '',
    };
    console.log('[mercadopago.return.cemento.payload]', payload);
    return payload;
  }

  private showModal(title: string, description: string, onClose: () => void): void {
    const modalRef = this.modalService.open(ModalAlertComponent, { centered: true });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.description = description;
    modalRef.componentInstance.headerCloseRunsFunction = true;
    modalRef.componentInstance.function = () => {
      this.modalService.dismissAll();
      onClose();
    };
  }
}
